import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { PiggyBank, CalendarCheck, ShieldCheck, LifeBuoy, Clock, XCircle } from 'lucide-react';
import { StokvelTerms } from './StokvelTerms';
import { StokvelApplyDialog } from './StokvelApplyDialog';
import { StokvelWithdrawalDialog } from './StokvelWithdrawalDialog';
import { StokvelMemberView } from './StokvelMemberView';
import { rand, dateLabel } from './format';

const HOW = [
  { icon: PiggyBank, title: 'Save monthly', text: 'A fixed amount is deducted from your net salary each payday, January to November.' },
  { icon: ShieldCheck, title: 'Safe & interest-bearing', text: 'Funds sit in a dedicated "Savings Stokvel" bank account earning interest, plus qualifying Employer Contributions.' },
  { icon: CalendarCheck, title: 'December payout', text: 'Your capital, vested Employer Contributions and pro-rata interest are paid by EFT before the festive season.' },
  { icon: LifeBuoy, title: 'Emergency safety net', text: 'In a verified emergency you may withdraw up to 80% of your capital — once per cycle.' },
];

const Intro = ({ tiers, onApply, declined }) => (
  <div className="space-y-6">
    {declined && (
      <div className="flex gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-900" data-testid="stokvel-declined-banner">
        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>Your previous application was declined{declined.decision_reason ? `: ${declined.decision_reason}` : '.'} You may apply again.</div>
      </div>
    )}
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {HOW.map(h => (
        <div key={h.title} className="rounded-xl border border-stone-200 p-4 bg-stone-50">
          <h.icon className="w-6 h-6 text-amber-700 mb-2" />
          <div className="font-semibold text-stone-800 text-sm">{h.title}</div>
          <p className="text-xs text-stone-600 mt-1">{h.text}</p>
        </div>
      ))}
    </div>
    <div className="text-sm text-stone-700">
      Contribution tiers: {tiers.map(t => <span key={t} className="inline-block mr-2 px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-semibold">{rand(t)}</span>)}
    </div>
    <div>
      <h3 className="font-semibold text-stone-800 mb-1">Terms &amp; Conditions</h3>
      <div className="border border-stone-200 rounded-lg px-3"><StokvelTerms /></div>
    </div>
    <Button onClick={onApply} data-testid="stokvel-apply-btn" className="bg-stone-800 hover:bg-stone-900 text-white">Apply to join</Button>
  </div>
);

export const StokvelStaffTab = () => {
  const [data, setData] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [wdOpen, setWdOpen] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/stokvel/me`)).data); setError(''); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to load stokvel'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="p-6 text-sm text-rose-700" data-testid="stokvel-error">{error}</div>;
  if (!data) return <div className="p-6 text-sm text-stone-500">Loading…</div>;
  const m = data.membership;

  return (
    <div className="bg-white rounded-xl shadow-md border border-stone-200 p-4 sm:p-6 space-y-6" data-testid="stokvel-staff-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-stone-800">TMC Savings Stokvel</h2>
          <p className="text-xs sm:text-sm text-stone-600 mt-1">Payroll-deducted savings for a single payout ahead of December.</p>
        </div>
        <div className="text-right text-xs text-stone-500" data-testid="stokvel-payout-date">
          Payout date {new Date().getFullYear()}: <b className="text-stone-800">{dateLabel(data.settings.payout_date)}</b>
        </div>
      </div>
      {!m && <Intro tiers={data.settings.tiers} onApply={() => setApplyOpen(true)} declined={data.last_declined} />}
      {m?.status === 'pending' && (
        <div className="flex gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="stokvel-pending-card">
          <Clock className="w-5 h-5 text-amber-700" />
          <div className="text-sm text-amber-900">
            Your application for <b>{rand(m.monthly_amount)}</b>/month was submitted on {dateLabel(m.applied_at)} and is awaiting management review.
          </div>
        </div>
      )}
      {m?.status === 'ended' && (
        <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg bg-stone-50 border border-stone-200 text-sm" data-testid="stokvel-ended-banner">
          <span>Your membership ended on {dateLabel(m.end_date)}{m.end_reason ? ` (${m.end_reason})` : ''}. Your contribution history is shown below.</span>
          <Button size="sm" onClick={() => setApplyOpen(true)} data-testid="stokvel-reapply-btn" className="bg-stone-800 hover:bg-stone-900 text-white">Apply again</Button>
        </div>
      )}
      {(m?.status === 'active' || m?.status === 'ended') && (
        <StokvelMemberView data={data} onWithdraw={() => setWdOpen(true)} />
      )}
      <StokvelApplyDialog open={applyOpen} onOpenChange={setApplyOpen} tiers={data.settings.tiers} onDone={load} />
      <StokvelWithdrawalDialog open={wdOpen} onOpenChange={setWdOpen} categories={data.categories}
        summary={data.summary} capPct={data.settings.emergency_cap_pct} onDone={load} />
    </div>
  );
};
